import { CertificateReferenceNumberField } from "@/services/backend/vbs/certificates/components/certificate-reference-number-field";
import { useState } from "react";
import { Certificate } from "@/services/backend/vbs/certificates/types/certificate";
import {
  UpdateIssueDateRequest,
  UpdateSegmentsRequest,
  UpdateWorkEndedAtRequest,
  UpdateWorkStartedAtRequest,
  useVbsCertificateShowQuery,
  useVbsCertificateUpdateIssueDateMutation,
  useVbsCertificateUpdatePostalAddressMutation,
  useVbsCertificateUpdateSegmentsMutation,
  useVbsCertificateUpdateServiceAddressMutation,
  useVbsCertificateUpdateWorkEndedAtMutation,
  useVbsCertificateUpdateWorkStartedAtMutation,
} from "@/services/backend/vbs/certificates/api/certificate-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import t from "@/lang/lang";
import {
  SegmentKind,
  SegmentKindList,
} from "@/services/backend/vbs/treatments/types/segmentKind";
import { ServiceAddressCardWithMutation } from "@/services/backend/addresses/components/service-address-card";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { PostalAddressCardWithMutation } from "@/services/backend/addresses/components/postal-address-card";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { FieldErrors } from "@/shared/components/field/field";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";

export function CertificateComponent(props: {
  certificateId: string;
  disabled?: boolean;
}) {
  const { certificateId, disabled = false } = props;
  const { data, isLoading, error } = useVbsCertificateShowQuery({
    id: certificateId,
  });

  if (isLoading) {
    return <CertificateSkeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="space-y-2">
      <CertificateEditForm certificate={data!} disabled={disabled} />
      <PostalAddressCardWithMutation
        id={data!.id}
        address={data!.postalAddress}
        mutation={useVbsCertificateUpdatePostalAddressMutation}
        disabled={disabled}
      />
      <ServiceAddressCardWithMutation
        id={data!.id}
        address={data!.serviceAddress}
        mutation={useVbsCertificateUpdateServiceAddressMutation}
        disabled={disabled}
      />
    </div>
  );
}

function CertificateSkeleton() {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("Zertifikat Daten")}</CardTitle>
          <CardDescription>{t("Basisdaten des Zertifikats")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function CertificateEditForm(props: {
  certificate: Certificate;
  disabled?: boolean;
}) {
  const { certificate, disabled = false } = props;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Zertifikat Daten")}</CardTitle>
        <CardDescription>{t("Basisdaten des Zertifikats")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <CertificateReferenceNumberField
          certificate={certificate}
          disabled={disabled}
        />
        <IssueDateInput
          certificateId={certificate.id}
          issueDate={certificate.issueDate}
          disabled={disabled}
        />
        <WorkStartDateInput
          certificateId={certificate.id}
          workStartedAt={certificate.workStartedAt}
          disabled={disabled}
        />
        <WorkEndDateInput
          certificateId={certificate.id}
          workEndedAt={certificate.workEndedAt}
          disabled={disabled}
        />
        <SegmentSelectionComponent
          certificate={certificate}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}

function IssueDateInput(props: {
  certificateId: string;
  issueDate: string;
  disabled?: boolean;
}) {
  const { certificateId, issueDate, disabled = false } = props;
  const [request, setRequest] = useState<UpdateIssueDateRequest>({
    id: certificateId,
    issueDate,
  });
  const mutation = useVbsCertificateUpdateIssueDateMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: certificateId,
    issueDate,
  });

  return (
    <DatePickerField
      label={t("Datum der Ausstellung")}
      nullable
      date={request.issueDate || undefined}
      onDateChange={(date) => setRequest({ ...request, issueDate: date ?? "" })}
      fieldName="issueDate"
      disabled={disabled}
    />
  );
}

function WorkStartDateInput(props: {
  certificateId: string;
  workStartedAt: string;
  disabled?: boolean;
}) {
  const { certificateId, workStartedAt, disabled = false } = props;
  const [request, setRequest] = useState<UpdateWorkStartedAtRequest>({
    id: certificateId,
    workStartedAt,
  });
  const mutation = useVbsCertificateUpdateWorkStartedAtMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: certificateId,
    workStartedAt,
  });

  return (
    <DatePickerField
      label={t("Begin der Arbeiten")}
      nullable
      date={request.workStartedAt || undefined}
      onDateChange={(date) =>
        setRequest({ ...request, workStartedAt: date ?? "" })
      }
      fieldName="workStartedAt"
      disabled={disabled}
    />
  );
}

function WorkEndDateInput(props: {
  certificateId: string;
  workEndedAt: string;
  disabled?: boolean;
}) {
  const { certificateId, workEndedAt, disabled = false } = props;
  const [request, setRequest] = useState<UpdateWorkEndedAtRequest>({
    id: certificateId,
    workEndedAt,
  });
  const mutation = useVbsCertificateUpdateWorkEndedAtMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: certificateId,
    workEndedAt,
  });

  return (
    <DatePickerField
      label={t("Ende der Arbeiten")}
      nullable
      date={request.workEndedAt || undefined}
      onDateChange={(date) =>
        setRequest({ ...request, workEndedAt: date ?? "" })
      }
      fieldName="workEndedAt"
      disabled={disabled}
    />
  );
}

function SegmentSelectionComponent(props: {
  certificate: Certificate;
  disabled?: boolean;
}) {
  const { certificate, disabled = false } = props;
  const [request, setRequest] = useState<UpdateSegmentsRequest>({
    id: certificate.id,
    segments: certificate.segments,
  });
  const mutation = useVbsCertificateUpdateSegmentsMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: certificate.id,
    segments: certificate.segments,
  });
  const { errors, invalid, reset } = useFieldErrors("segments");

  const segmentSelected = (segmentKind: SegmentKind): boolean =>
    request.segments.includes(segmentKind);

  const updateSelection = (
    segmentKind: SegmentKind,
    selection: boolean,
  ): void => {
    reset();
    if (selection) {
      if (request.segments.includes(segmentKind)) {
        return;
      }
      setRequest({
        id: request.id,
        segments: [...request.segments, segmentKind],
      });
    } else {
      setRequest({
        id: request.id,
        segments: request.segments.filter((segment) => segment !== segmentKind),
      });
    }
  };

  return (
    <div className="grid gap-1.5">
      <Label>{t("Behandelte Bauteilgruppen")}</Label>
      <div className="space-y-2">
        {SegmentKindList.map((segmentKind) => (
          <div className="flex items-center space-x-2" key={segmentKind}>
            <Switch
              checked={segmentSelected(segmentKind)}
              onCheckedChange={(newValue) =>
                updateSelection(segmentKind, newValue)
              }
              aria-invalid={invalid || undefined}
              disabled={disabled}
            >
              <span className="sr-only">Enable notifications</span>
            </Switch>
            <Label>{t(segmentKind)}</Label>
          </div>
        ))}
      </div>
      <FieldErrors errors={errors} />
    </div>
  );
}
